function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Page, Button, Typography, TextField, MenuItem, Card, Divider } from 'seagull';
import { cityNameValidation, companyNameValidation, postalCodeValidation, streetNameValidation } from 'validators';
import { useGlobalContext, useStore, useStoreActions } from "../../contexts";
import { selectors } from "../../store/state-selectors";
import { ErrorText, ownershipTypes, states } from "../../constants";
import { Intent } from "../../infra/access-control";
import { isBankAccountConnected } from "../../store/store.model";
import { handleRequest } from "../../infra/utils";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import { makeSentryIgnoredError } from "../../components/ErrorBoundaryPage";
import { useUploadLogo } from "../../hooks/useUploadLogo";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useConnectBankAccount } from "../../hooks/useConnectBankAccount";
import styles from "./BusinessDetails.module.scss";
const formId = 'business-details-form';
const FieldTitle = props => /*#__PURE__*/React.createElement(Typography, _extends({
  variant: "h6",
  weight: "normal"
}, props));
const testPrefix = 'settings-business-details';
const title = 'Business details';
const ownershipTypeList = ownershipTypes.map(_ref => {
  let {
    value,
    display
  } = _ref;
  return {
    value,
    label: display
  };
});
export function BusinessDetails() {
  const worksAt = useStore(selectors.user.business);
  if (!worksAt) throw makeSentryIgnoredError('no business');
  return /*#__PURE__*/React.createElement(BusinessDetailsWithWorksAt, {
    worksAt: worksAt
  });
}
function BusinessDetailsWithWorksAt(_ref2) {
  let {
    worksAt
  } = _ref2;
  const globalContext = useGlobalContext();
  const isVendor = useStore(selectors.user.isVendor);
  const payoutBankAccount = useStore(selectors.user.payoutBankAccount);
  const {
    uploadLogo
  } = useUploadLogo(worksAt);
  const {
    api,
    setWorksAt,
    logBI
  } = useStoreActions();
  const {
    EXPOSE_BANK_ACCOUNT
  } = useFeatureFlags();
  const {
    Description,
    ConnectButton,
    BankAccountDetails
  } = useConnectBankAccount();
  useEffect(() => {
    document.title = title;
  }, []);
  const businessOwnership = useMemo(() => ownershipTypeList.find(_ref3 => {
    let {
      value
    } = _ref3;
    return value === worksAt.ownership;
  }) || null, [worksAt.ownership]);
  const {
    control,
    formState,
    handleSubmit,
    setValue
  } = useForm({
    mode: 'all',
    defaultValues: getInitialFormState()
  });
  function getImageDataUrl(file) {
    return new Promise((resolve, reject) => {
      if (!file || !file.name) {
        reject('No file selected');
      } else if (!file.name.match(/\.png$/)) {
        reject('Invalid file type: must be .png');
      } else {
        const reader = new FileReader();
        reader.onload = function (e) {
          var _e$target;
          if (typeof ((_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.result) === 'string') {
            var _e$target2;
            resolve((_e$target2 = e.target) === null || _e$target2 === void 0 ? void 0 : _e$target2.result);
          } else {
            reject(ErrorText.UnexpectedErrorTryAgain);
          }
        };
        reader.onerror = () => {
          reject('File could open, please choose another one or try again');
        };
        reader.readAsDataURL(file);
      }
    });
  }
  function getInitialFormState() {
    var _worksAt$postalAddres, _worksAt$postalAddres2, _worksAt$postalAddres3, _worksAt$postalAddres4, _worksAt$postalAddres5, _worksAt$postalAddres6;
    return {
      businessName: worksAt.name,
      address1: (_worksAt$postalAddres = worksAt.postalAddress) === null || _worksAt$postalAddres === void 0 ? void 0 : _worksAt$postalAddres.addressOne,
      address2: (_worksAt$postalAddres2 = (_worksAt$postalAddres3 = worksAt.postalAddress) === null || _worksAt$postalAddres3 === void 0 ? void 0 : _worksAt$postalAddres3.addressTwo) !== null && _worksAt$postalAddres2 !== void 0 ? _worksAt$postalAddres2 : '',
      city: (_worksAt$postalAddres4 = worksAt.postalAddress) === null || _worksAt$postalAddres4 === void 0 ? void 0 : _worksAt$postalAddres4.city,
      state: (_worksAt$postalAddres5 = worksAt.postalAddress) === null || _worksAt$postalAddres5 === void 0 ? void 0 : _worksAt$postalAddres5.state,
      postalCode: (_worksAt$postalAddres6 = worksAt.postalAddress) === null || _worksAt$postalAddres6 === void 0 ? void 0 : _worksAt$postalAddres6.postalCode,
      ownership: businessOwnership,
      logoUrl: worksAt.logoUrl
    };
  }
  async function onSubmit(_ref4) {
    var _initialFormState$own;
    let {
      businessName,
      address1,
      address2,
      city,
      state,
      postalCode,
      ownership,
      logoFile,
      logoUrl
    } = _ref4;
    logBI(10420);
    if (logoUrl && logoFile && worksAt && logoUrl !== worksAt.logoUrl) {
      await uploadLogo({
        file: logoFile,
        asDataURL: logoUrl
      }, () => setValue('logoUrl', worksAt.logoUrl));
    }
    const params = {};
    const initialFormState = getInitialFormState();
    if (businessName !== initialFormState.businessName) {
      params.name = businessName;
    }
    if (address1 !== initialFormState.address1 || address2 !== initialFormState.address2 || city !== initialFormState.city || state !== initialFormState.state || postalCode !== initialFormState.postalCode) {
      var _worksAt$postalAddres7;
      params.postalAddress = {
        address1,
        address2: address2 || null,
        city,
        state,
        postalCode,
        country: ((_worksAt$postalAddres7 = worksAt.postalAddress) === null || _worksAt$postalAddres7 === void 0 ? void 0 : _worksAt$postalAddres7.country) || 'US'
      };
    }
    if (ownership && ownership.value !== ((_initialFormState$own = initialFormState.ownership) === null || _initialFormState$own === void 0 ? void 0 : _initialFormState$own.value)) {
      params.ownership = ownership.value;
    }
    if (!Object.keys(params).length) return;
    const {
      success
    } = await handleRequest(api.vendor.updateBusinessDetails(worksAt.id, params));
    if (success) {
      var _params$name, _params$ownership, _worksAt$postalAddres8, _worksAt$postalAddres9;
      setWorksAt(_objectSpread(_objectSpread({}, worksAt), {}, {
        name: (_params$name = params.name) !== null && _params$name !== void 0 ? _params$name : worksAt.name,
        ownership: (_params$ownership = params.ownership) !== null && _params$ownership !== void 0 ? _params$ownership : worksAt.ownership,
        postalAddress: params.postalAddress ? {
          addressOne: params.postalAddress.address1,
          addressTwo: params.postalAddress.address2,
          city: params.postalAddress.city,
          state: params.postalAddress.state,
          postalCode: params.postalAddress.postalCode,
          country: (_worksAt$postalAddres8 = (_worksAt$postalAddres9 = worksAt.postalAddress) === null || _worksAt$postalAddres9 === void 0 ? void 0 : _worksAt$postalAddres9.country) !== null && _worksAt$postalAddres8 !== void 0 ? _worksAt$postalAddres8 : 'US'
        } : worksAt.postalAddress
      }));
    }
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Page, {
    header: title,
    "data-testid": testPrefix + '-screen',
    skin: "secondary",
    actions: /*#__PURE__*/React.createElement(Button, {
      intent: Intent.UpdateBusinessDetails,
      type: "submit",
      form: formId,
      disabled: !formState.isValid,
      loading: formState.isSubmitting,
      "data-testid": testPrefix + '-btn-save-changes'
    }, "Save changes")
  }, /*#__PURE__*/React.createElement("form", {
    id: formId,
    onSubmit: handleSubmit(onSubmit)
  }, /*#__PURE__*/React.createElement(FieldTitle, null, "Brand logo"), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "logoUrl",
    render: _ref5 => {
      let {
        field
      } = _ref5;
      return /*#__PURE__*/React.createElement("div", {
        className: styles.brandElementsContainer
      }, /*#__PURE__*/React.createElement("div", {
        className: styles.circle
      }, /*#__PURE__*/React.createElement("div", {
        className: styles.logoImageBorder
      }), field.value && /*#__PURE__*/React.createElement("div", {
        className: styles.logoImageContainer,
        style: {
          backgroundImage: "url(".concat(field.value, ")")
        },
        "data-testid": testPrefix + '-logo-preview'
      }), /*#__PURE__*/React.createElement("label", {
        className: styles.logoDragAndDropLabel
      }, /*#__PURE__*/React.createElement("input", {
        ref: field.ref,
        type: "file",
        accept: ".png",
        onChange: async e => {
          var _e$target$files;
          const file = (_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files[0];
          if (!file) return;
          try {
            const imageUrl = await getImageDataUrl(file);
            field.onChange(imageUrl);
            setValue('logoFile', file);
          } catch (error) {
            globalContext.showErrorToast(error);
            e.target.value = '';
          }
        },
        className: styles.inputFile,
        "data-testid": testPrefix + '-logo-upload'
      }), !field.value && /*#__PURE__*/React.createElement(Typography, null, "Add logo"))));
    }
  }), /*#__PURE__*/React.createElement(Card, {
    className: styles.fieldsLayout,
    autoHeight: true,
    padding: "m",
    title: "Details"
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "businessName",
    rules: companyNameValidation('Business name', {
      required: 'Please enter your business name'
    }),
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
      label: "Business name",
      "data-testid": testPrefix + '-business-name'
    }))
  }), isVendor && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "address1",
    rules: streetNameValidation(undefined, {
      required: 'Please enter a street address'
    }),
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
      label: "Address 1",
      "data-testid": testPrefix + '-address1'
    }))
  }), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "address2",
    rules: streetNameValidation(),
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
      label: "Address 2",
      "data-testid": testPrefix + '-address2'
    }))
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.address
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "postalCode",
    rules: postalCodeValidation(undefined, {
      required: 'Please enter a postal code'
    }),
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
      label: "Postal code",
      "data-testid": testPrefix + '-postal-code'
    }))
  }), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "city",
    rules: cityNameValidation(undefined, {
      required: 'Please enter a city'
    }),
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
      label: "City",
      "data-testid": testPrefix + '-city'
    }))
  }), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "state",
    rules: {
      required: true
    },
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({
      select: true,
      label: "State",
      menuMaxHeight: 300
    }, connectControllerToTextField(p), {
      "data-testid": testPrefix + '-dropdown-state'
    }), states.map(state => /*#__PURE__*/React.createElement(MenuItem, {
      key: state,
      value: state
    }, state)))
  })))), EXPOSE_BANK_ACCOUNT && isVendor && /*#__PURE__*/React.createElement(Card, {
    className: styles.fieldsLayout,
    autoHeight: true,
    padding: "m",
    disableSeparator: true,
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      weight: "normal",
      variant: "h5"
    }, "Bank account information"), /*#__PURE__*/React.createElement(Description, {
      testid: "bank-account-details-title",
      linkTestid: "bank-account-details-read-more"
    })),
    "data-testid": 'bank-account-details'
  }, /*#__PURE__*/React.createElement(Divider, {
    margin: 0
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.bankDetails
  }, isBankAccountConnected(payoutBankAccount) ? /*#__PURE__*/React.createElement(BankAccountDetails, {
    payoutBankAccount: payoutBankAccount
  }) : /*#__PURE__*/React.createElement(Typography, null, "No bank account connected."), /*#__PURE__*/React.createElement(ConnectButton, {
    className: styles.bankActions,
    enterManuallyTestid: "enter-manually",
    testid: "connect-bank-account"
  }))))));
}